import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { type ContainersError, containersChange, marginText, sizesText } from "./workOrders";
import { SkeletonSection } from "./Skeleton";

/** Design 8.6: the standard container sizes and the margin of the recommended size. */
export function ContainerSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.ContainerSettings | "failed" | undefined>(undefined);
  const [sizes, setSizes] = useState("");
  const [margin, setMargin] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [problem, setProblem] = useState<ContainersError | null>(null);
  const [saving, setSaving] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);
  const fields = useRef<Record<ContainersError["field"], HTMLInputElement | null>>({ sizes: null, margin: null });

  const show = (next: api.ContainerSettings) => {
    setFound(next);
    setSizes(sizesText(next.sizes));
    setMargin(marginText(next.margin));
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.containerSizes)
      .then(show, () => setFound("failed"));
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Containers" rows={6} />;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Containers</legend>
        <p className="error">Cannot load the container sizes. Try again later.</p>
      </fieldset>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const change = containersChange(sizes, margin);
    if ("field" in change) {
      setMessage(null);
      setProblem(change);
      fields.current[change.field]?.focus();
      return;
    }
    setProblem(null);
    setSaving(true);
    setMessage(null);
    try {
      show(await api.setContainerSizes(await user.getIdToken(), change));
      setMessage({ ok: true, text: "Saved. The recommended sizes use the new values." });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The container sizes were not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} noValidate data-testid="container-sizes">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Containers</legend>
        <div className="limit">
          <label htmlFor="container-sizes-input">Standard sizes (gal)</label>
          <input
            id="container-sizes-input"
            data-testid="container-sizes-input"
            ref={(element) => {
              fields.current.sizes = element;
            }}
            inputMode="numeric"
            value={sizes}
            aria-invalid={problem?.field === "sizes" || undefined}
            aria-describedby={problem?.field === "sizes" ? "container-sizes-error container-sizes-hint" : "container-sizes-hint"}
            onChange={(e) => {
              setSizes(e.target.value);
              setMessage(null);
              if (problem?.field === "sizes") setProblem(null);
            }}
          />
          {problem?.field === "sizes" && (
            <p id="container-sizes-error" role="alert" className="error" data-testid="container-sizes-error">
              {problem.text}
            </p>
          )}
          <p id="container-sizes-hint" className="hint">
            Whole gallons, with commas between them. From 10 to 2000 gal, at most 10 sizes. Default: {sizesText(found.defaults.sizes)}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="container-margin">Margin (%)</label>
          <input
            id="container-margin"
            data-testid="container-margin"
            ref={(element) => {
              fields.current.margin = element;
            }}
            inputMode="decimal"
            value={margin}
            aria-invalid={problem?.field === "margin" || undefined}
            aria-describedby={problem?.field === "margin" ? "container-margin-error container-margin-hint" : "container-margin-hint"}
            onChange={(e) => {
              setMargin(e.target.value);
              setMessage(null);
              if (problem?.field === "margin") setProblem(null);
            }}
          />
          {problem?.field === "margin" && (
            <p id="container-margin-error" role="alert" className="error" data-testid="container-margin-error">
              {problem.text}
            </p>
          )}
          <p id="container-margin-hint" className="hint">
            The recommended size holds the oil of one cycle plus this share. From 0 to 100%. Default:{" "}
            {marginText(found.defaults.margin)}%.
          </p>
        </div>
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-containers">
              {saving ? "Saving…" : "Save the sizes"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="containers-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
